import { create } from 'zustand';
import { LessonStep } from '../services/lessons/lesson.service';

interface LessonState {
  topic_id: number | null;
  level_id: number | null;
  sub_topic_id: number | null;
  instructions: string;
  steps: LessonStep[];
  
  setTopicId: (topic_id: number | null) => void;
  setLevelId: (level_id: number | null) => void;
  setSubTopicId: (sub_topic_id: number | null) => void;
  setInstructions: (instructions: string) => void;
  setSteps: (steps: LessonStep[]) => void;
  resetForm: () => void;
  
  addStep: () => void;
  updateStep: (stepNumber: number, data: Partial<LessonStep>) => void;
  removeStep: (stepNumber: number) => void;
  
  clearState: () => void;
  
  topicOptions: { value: string; label: string }[];
  setTopicOptions: (options: { value: string; label: string }[]) => void;
  isTopicsLoading: boolean;
  fetchTopics: (service: any) => Promise<void>;

  subTopicOptions: { value: string; label: string }[];
  setSubTopicOptions: (options: { value: string; label: string }[]) => void;
  isSubTopicsLoading: boolean;
  fetchSubTopics: (service: any, topicId: number, levelId?: number) => Promise<void>;
  
  levelOptions: { value: string; label: string }[];
  setLevelOptions: (options: { value: string; label: string }[]) => void;
  isLevelsLoading: boolean;
  fetchLevels: (service: any) => Promise<void>;
}

const getInitialStep = (stepNumber: number = 1): LessonStep => ({
  step_number: stepNumber,
  title: '',
  teacher_content: '',
  chart_url: '',
  quiz: {
    question: '',
    options: { a: '', b: '', c: '' },
    correct_option: '',
  },
  teacher_summary: '',
});

export const useLessonStore = create<LessonState>()((set) => ({
  topic_id: null,
  level_id: null,
  sub_topic_id: null,
  instructions: '',
  steps: [getInitialStep()],
  
  setTopicId: (id) => set({ topic_id: id }),
  setLevelId: (id) => set({ level_id: id }),
  setSubTopicId: (id) => set({ sub_topic_id: id }),
  setInstructions: (inst) => set({ instructions: inst }),
  setSteps: (steps) => set({ steps }),
  resetForm: () => set({ instructions: '', steps: [getInitialStep()] }),
  
  addStep: () =>
    set((state) => {
      const nextStepNum = state.steps.length > 0 
        ? Math.max(...state.steps.map(s => s.step_number)) + 1 
        : 1;
      return {
        steps: [...state.steps, { ...getInitialStep(), step_number: nextStepNum }],
      };
    }),
    
  updateStep: (stepNumber, data) =>
    set((state) => ({
      steps: state.steps.map((step) =>
        step.step_number === stepNumber ? { ...step, ...data } : step
      ),
    })),
    
  removeStep: (stepNumber) =>
    set((state) => ({
      steps: state.steps.filter((step) => step.step_number !== stepNumber),
    })),
    
  clearState: () => {
    localStorage.removeItem('shoonit-lesson-builder');
    set({ topic_id: null, level_id: null, sub_topic_id: null, instructions: '', steps: [getInitialStep()] });
  },

  topicOptions: [],
  setTopicOptions: (options) => set({ topicOptions: options }),
  isTopicsLoading: false,
  fetchTopics: async (service) => {
    const state = useLessonStore.getState();
    if (state.isTopicsLoading || state.topicOptions.length > 0) return;

    set({ isTopicsLoading: true });
    try {
      const topics = await service.getTopics();
      const formatted = topics.map((t: any) => ({ value: t.id.toString(), label: t.name }));
      set({ topicOptions: formatted, isTopicsLoading: false });
    } catch (error) {
      console.error("Failed to fetch topics in store:", error);
      set({ isTopicsLoading: false });
    }
  },

  subTopicOptions: [],
  setSubTopicOptions: (options) => set({ subTopicOptions: options }),
  isSubTopicsLoading: false,
  fetchSubTopics: async (service, topicId, levelId) => {
    if (!topicId) return;
    set({ isSubTopicsLoading: true });
    try {
      const subTopics = await service.getSubTopics(topicId, levelId);
      const formatted = subTopics.map((t: any) => ({ value: t.id.toString(), label: t.name }));
      set({ subTopicOptions: formatted, isSubTopicsLoading: false });
    } catch (error) {
      console.error("Failed to fetch sub-topics in store:", error);
      set({ isSubTopicsLoading: false });
    }
  },

  levelOptions: [],
  setLevelOptions: (options) => set({ levelOptions: options }),
  isLevelsLoading: false,
  fetchLevels: async (service) => {
    const state = useLessonStore.getState();
    if (state.isLevelsLoading || state.levelOptions.length > 0) return;

    set({ isLevelsLoading: true });
    try {
      const levels = await service.getLevels();
      const formatted = levels.map((l: any) => ({ value: l.id.toString(), label: l.name }));
      set({ levelOptions: formatted, isLevelsLoading: false });
    } catch (error) {
      console.error("Failed to fetch levels in store:", error);
      set({ isLevelsLoading: false });
    }
  },
}));
